// Interim bridge: interpret a client record as one or more engagements in the new
// model, so the engine can run against real data before migration. Accepts BOTH the
// snake_case DB shape (Node scripts) and the camelCase API shape (the app), so one
// mapper serves both. Phase 3 migration will make these explicit records.
//
// Mapping rules (from REVENUE_ENGINE_SPEC.md):
//   BWM guided-wealth: monthly -> subscription (recurring); if monthly is 0 but an
//     annual amount is set, that is an annual-pay subscription. investment fee -> AUM.
//     tax plan fee -> tax planning (committed).
//   BWM investment-only: the annual amount is the AUM fee.
//   BWM tax-plan: the annual amount is a committed tax-planning amount.
//   BTS cao -> Fractional CAO (recurring); bookkeeping/monthly -> bookkeeping (recurring);
//     tax-prep annual -> committed (tax prep or cleanup).
//   BIA -> commissions (actuals only). Non-Active status contributes nothing.

import { isInternalHousehold } from '../../lib/households.js';

const num = (v) => Number(v) || 0;

export function mapClientToEngagements(c) {
  if (c.status && c.status !== 'Active') return [];
  if (isInternalHousehold(c)) return [];   // the reserved internal household (hh-firm) is not revenue
  const g = (snake, camel) => (c[snake] !== undefined ? c[snake] : c[camel]);
  const div = c.division;
  const tier = g('service_tier', 'serviceTier') || '';
  const type = g('client_type', 'clientType') || '';
  const monthly = num(g('monthly_revenue', 'monthlyRevenue'));
  const annual = num(g('annual_revenue', 'annualRevenue'));
  const invFee = num(g('investment_fee_annual', 'investmentFeeAnnual'));
  const taxPlanFee = num(g('tax_plan_fee', 'taxPlanFee'));
  const priceHistory = g('price_history', 'priceHistory') || [];
  const dates = { startDate: g('start_date', 'startDate'), endDate: g('churned_date', 'churnedDate') || null };
  const engs = [];

  if (div === 'bwm') {
    if (monthly > 0) {
      engs.push({ division: 'bwm', category: 'subscription', pattern: 'recurring', monthlyRate: monthly, rateHistory: priceHistory, ...dates });
    } else if (annual > 0 && tier !== 'investment-only') {
      if (tier === 'tax-plan') {
        engs.push({ division: 'bwm', category: 'tax_planning', pattern: 'committed', amount: annual, revenueYear: null });
      } else {
        engs.push({ division: 'bwm', category: 'subscription', pattern: 'recurring', monthlyRate: annual / 12, rateHistory: [], ...dates });
      }
    }
    if (invFee > 0) {
      engs.push({ division: 'bwm', category: 'investment_management', pattern: 'aum', annualFee: invFee, ...dates });
    } else if (tier === 'investment-only' && annual > 0) {
      engs.push({ division: 'bwm', category: 'investment_management', pattern: 'aum', annualFee: annual, ...dates });
    }
    if (taxPlanFee > 0) {
      engs.push({ division: 'bwm', category: 'tax_planning', pattern: 'committed', amount: taxPlanFee, revenueYear: null });
    }
  } else if (div === 'bts') {
    if (type === 'cao') {
      engs.push({ division: 'bts', category: 'cao', pattern: 'recurring', monthlyRate: monthly, rateHistory: priceHistory, ...dates });
    } else if (type === 'bookkeeping' || monthly > 0) {
      engs.push({ division: 'bts', category: 'bookkeeping', pattern: 'recurring', monthlyRate: monthly, rateHistory: priceHistory, ...dates });
    } else if (annual > 0) {
      engs.push({ division: 'bts', category: 'tax_preparation', pattern: 'committed', amount: annual, revenueYear: null });
    }
  } else if (div === 'bia') {
    engs.push({ division: 'bia', category: 'commissions', pattern: 'actuals', amount: annual, ...dates });
  }
  return engs;
}
